import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import ImageFrame from "@/components/ImageFrame";
import CTABand from "@/components/CTABand";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLines, ChapterLabel } from "@/components/motion";
import { IMAGES, BOOKING_URL } from "@/data/content";

const AREAS_OF_REGULATION = [
  ["Opmærksomhed", "At kunne rette fokus mod en opgave og fastholde det uden at kæmpe for det."],
  ["Søvn", "At kunne skrue ned for systemet om aftenen og glide over i dyb, restituerende søvn."],
  ["Stressrespons", "At kunne aktivere kroppens beredskab, når det er nødvendigt, og deaktivere det igen."],
  ["Følelser", "At kunne møde stærke følelser uden at blive overvældet eller koble fra."],
  ["Hverdagsfunktion", "Overskud til skole, arbejde, relationer og alt det, der fylder en almindelig dag."],
];

const Hjerneregulering = () => (
  <main data-testid="page-hjerneregulering">
    <PageHero
      testId="hero-hjerneregulering"
      overline="Hjerneregulering"
      title={["Hjernen er et", "selvregulerende", "system."]}
      lead="Hele døgnet regulerer hjernen opmærksomhed, søvn, stressrespons og følelser. Forståelsen af den regulering er kernen i alt, centret gør."
    />

    <section className="border-b border-hairline">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8 lg:py-28">
        <div className="lg:col-span-6">
          <Reveal>
            <ChapterLabel number="01" label="Selvregulering" />
          </Reveal>
          <h2 className="mt-8 font-serif text-3xl leading-[1.1] tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
            <MaskedLines mode="view" lines={["Hvad betyder", "selvregulering?"]} />
          </h2>
          <Reveal delay={0.15} className="mt-8 space-y-5 text-base leading-relaxed text-graphite">
            <p>
              Selvregulering er hjernens evne til at tilpasse sin egen aktivitet til situationen. At tænde
              for opmærksomheden, når der ligger en opgave foran os. At slippe alarmberedskabet, når faren
              er overstået. At glide ned i søvn, når dagen er slut.
            </p>
            <p>
              Det er ikke en egenskab, man enten har eller ikke har. Det er et dynamisk samspil i
              nervesystemet, som påvirkes af søvn, stress, omgivelser og erfaring, og som kan trænes
              ligesom en muskel.
            </p>
          </Reveal>
          <div className="mt-12">
            {AREAS_OF_REGULATION.map(([title, desc], i) => (
              <Reveal key={title} delay={i * 0.05}>
                <div className="grid grid-cols-[3rem_1fr] gap-4 border-t border-hairline py-6 sm:grid-cols-[4rem_1fr]">
                  <span className="font-mono text-xs tracking-[0.2em] text-muted">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl tracking-tight text-charcoal">{title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-graphite">{desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5 lg:col-start-8">
          <Reveal delay={0.1}>
            <ImageFrame
              src={IMAGES.overhead}
              alt="Neurofeedback-session med sensorer på hovedbunden"
              caption="Fig. 01 — Måling af hjernens aktivitet"
              testId="hjerneregulering-regulation-img"
              tone="none"
            />
          </Reveal>
        </div>
      </div>
    </section>

    <section className="bg-obsidian text-[#F5F5F0]">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-32">
        <Reveal>
          <ChapterLabel number="02" label="Neuroplasticitet" dark />
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <h2 className="font-serif text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
              <MaskedLines
                mode="view"
                lines={[
                  "Hjernen formes af det,",
                  <span key="d" className="font-light italic text-[#A3A299]">
                    den oplever.
                  </span>,
                ]}
              />
            </h2>
            <Reveal delay={0.15} className="mt-8 max-w-2xl space-y-5 text-base leading-relaxed text-[#A3A299]">
              <p>
                Hjernen er ikke færdigudviklet ved en bestemt alder. Gennem hele livet styrkes og svækkes
                forbindelserne mellem nervecellerne alt efter, hvad vi gør, gentager og oplever. Det kalder
                man neuroplasticitet, og det er baggrunden for, at træning overhovedet giver mening.
              </p>
              <p>
                Det gælder i begge retninger: De mønstre, hjernen øver, bliver de stærkeste. Uanset om det
                er ro, fokus og regulering eller uro, alarm og tankemylder.
              </p>
            </Reveal>
          </div>
          <div className="flex items-end lg:col-span-5">
            <Reveal delay={0.2}>
              <blockquote className="border-l border-darkborder pl-8">
                <p className="font-serif text-3xl font-light italic leading-snug text-[#F5F5F0] sm:text-4xl">
                  Hjernen er dynamisk. Den formes af erfaring og kan forandre sig hele livet.
                </p>
              </blockquote>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    <section>
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8 lg:py-28">
        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <ImageFrame
              src={IMAGES.trainingDetail}
              alt="Ghazal Samii under neurofeedbacktræning med sensorer"
              caption="Fig. 02 — Ro er en tilstand, der kan trænes"
              testId="hjerneregulering-space-img"
              ratio="aspect-[3/4]"
              tone="none"
            />
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal>
            <ChapterLabel number="03" label="Når balancen vipper" />
          </Reveal>
          <h2 className="mt-8 font-serif text-3xl leading-[1.1] tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
            <MaskedLines mode="view" lines={["Det er ikke vilje.", "Det er regulering."]} />
          </h2>
          <Reveal delay={0.15} className="mt-8 space-y-5 text-base leading-relaxed text-graphite">
            <p>
              Langvarig stress, overstimulering eller udfordringer som ADHD og autisme kan gøre reguleringen
              tungere. Hjernen kan hænge fast i højeste gear eller have svært ved at komme op i gear, når
              det gælder.
            </p>
            <p>
              Det er ikke en karakterfejl, og det er ikke manglende vilje. Det er regulering, der har brug
              for træning. Og fordi hjernen er dynamisk, kan træning være en meningsfuld vej at arbejde
              med den.
            </p>
            <p>
              Hos Center for Hjerneregulering sker det med ILF Neurofeedback (Infra-Low Frequency) efter
              Othmer-metoden, hvor hjernen får præcis feedback om sin egen aktivitet i realtid.
            </p>
          </Reveal>
          <Reveal delay={0.25} className="mt-8">
            <Link
              to="/neurofeedback"
              data-testid="hjerneregulering-link-neurofeedback"
              className="group inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-charcoal"
            >
              <span className="border-b border-charcoal pb-1">Sådan virker neurofeedback</span>
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>

    <Marquee
      items={["Opmærksomhed", "Søvn", "Stressrespons", "Følelser", "Hverdagsfunktion", "Balance"]}
      dark
      testId="hjerneregulering-marquee"
    />
    <CTABand
      title="Er du nysgerrig på, om neurofeedback kunne være relevant for dig?"
      sub="Book en uforpligtende indledende samtale, hvor vi taler om din situation, hvad du gerne vil arbejde med, og hvordan et individuelt neurofeedbackforløb kan se ud."
      ctaLabel="Book en samtale"
      ctaTo={BOOKING_URL || "/kontakt"}
      ctaTestId="cta-band-book-btn"
    />
  </main>
);

export default Hjerneregulering;
